/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { jsx } from '@emotion/react';

import * as React from 'react';

import _ from 'lodash';

import type { StripeTaxIdType } from 'js/constants/stripeTaxIdTypes';
import STRIPE_TAX_ID_TYPES, { stripeTaxIdTypeMap } from 'js/constants/stripeTaxIdTypes';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import type { Theme } from '@coursera/cds-core';
import { Grid, Link, SelectField, SelectOption, Typography, Typography2 } from '@coursera/cds-core';
import { Checkbox, Radio, RadioGroup, TextInput } from '@coursera/coursera-ui';

import { isEmail } from 'bundles/enterprise-legacy-learner-home/utils/validator';
import Autocomplete from 'bundles/internal-enterprise/components/lead/Autocomplete';
import type { Invoice } from 'bundles/internal-enterprise/components/lead/EnterpriseLeadContainer';
import { EnterpriseLeadContext } from 'bundles/internal-enterprise/components/lead/EnterpriseLeadContainer';
import EnterpriseLeadInvoiceSummary from 'bundles/internal-enterprise/components/lead/EnterpriseLeadSummary';
import useDebounce from 'bundles/internal-enterprise/components/lead/useDebounce';
import { useInvoicePrice, useUserInfo } from 'bundles/internal-enterprise/components/lead/utils';
import Button from 'bundles/internal-ui/components/buttons/Button';
import countries from 'bundles/page/common/countries';

import 'css!./__styles__/EnterpriseLeadInvoiceForm';
import 'css!js/vendor/styl/coursera-ui.v0-1-4';

const INPUT_DEBOUNCE_DELAY = 500;

type Props = {
  params: {
    leadId: string;
  };
};

const styles: Record<string, Interpolation<Theme>> = {
  root: (theme) => ({
    margin: 'var(--cds-spacing-300)',
    width: 1000,
  }),
};

const organizationTypes = [
  { label: 'Coursera for Business (C4B)', value: 'BUSINESS' },
  { label: 'Coursera for Campus (C4C)', value: 'CAMPUS' },
  { label: 'Coursera for Government (C4G)', value: 'GOVERNMENT' },
];

const EnterpriseLeadInvoiceForm: React.FC<Props> = (props) => {
  const router = useRouter();

  const { lead, invoice, updateInvoice, setPurchaseUserId } = React.useContext(EnterpriseLeadContext);

  const [selectedTaxIdType, setSelectedTaxIdType] = React.useState<StripeTaxIdType | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [allowSuperuserEdit, setAllowSuperuserEdit] = React.useState(false);

  const debouncedEmail = useDebounce(invoice.customerEmail, INPUT_DEBOUNCE_DELAY);
  const { userId: customerUserId, orgs: customerOrgs } = useUserInfo(debouncedEmail);

  const { params } = props;

  React.useEffect(() => {
    // Redirect back to lead search if lead is not available (e.g. after refresh)
    if (!lead || lead.id !== params.leadId) {
      router.push('/internal/enterprise/lead');
    }
  }, [lead, params.leadId, router]);

  // Update purchaseUserId if the Email is changed manually
  React.useEffect(() => {
    if (customerUserId) {
      setPurchaseUserId(customerUserId);
    }
  }, [customerUserId, setPurchaseUserId]);

  // Update price when seats or country changed
  const debouncedSeats = useDebounce(invoice.seats, INPUT_DEBOUNCE_DELAY);
  const invoicePrice = useInvoicePrice(debouncedSeats, invoice.country?.value, customerUserId);

  // Update invoice when invoicePrice or discretionary discount changes
  React.useEffect(
    () => {
      if (invoicePrice) {
        // If server returned price...
        const priceType = invoicePrice.unitPrice === '399.00' ? 'standard' : 'geo';

        // use the price returned from server
        updateInvoice({
          unitPrice: invoicePrice.unitPrice,
          amountTotal: invoicePrice.amountTotal,
          currencyCode: invoicePrice.currencyCode,
          priceType,
        });
      } else {
        // server hasn't return price. this is the case where country/#seats are invalid
        updateInvoice({
          unitPrice: '',
          amountTotal: '',
          currencyCode: '',
        });
      }
    }, // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoicePrice]
  );

  const handleTextChange = (name: keyof Invoice) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    updateInvoice({ [name]: value });
  };

  const handleTextBlur = (name: keyof Invoice) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    updateInvoice({ [name]: value.trim() });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    updateInvoice({ customerEmail: value.toLowerCase() });
  };

  const handleEmailBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    updateInvoice({ customerEmail: value.trim().toLowerCase() });
  };

  const handleSeatsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const seats = parseInt(value.trim(), 10) || 0;
    updateInvoice({ seats });
  };

  const handleRadioChange = (name: string) => (e: $TSFixMe) => {
    const { value } = e.target;
    updateInvoice({ [name]: value });
  };

  const handleToggleEdit = () => {
    setAllowSuperuserEdit((prevState) => !prevState);
  };

  const handleCountryChange = (country: { label: string; value: string } | null) => {
    updateInvoice({
      taxIdTypeCode: '',
      country: country || undefined,
    });
    setSelectedTaxIdType(null);
  };

  const findCountry = (countryName?: string) => {
    if (!countryName) return undefined;
    if (countryName.startsWith('United States')) {
      return _.find(countries, (country) => country.name.startsWith(countryName));
    }

    return _.find(countries, (country) => country.name === countryName);
  };

  const handleExistingOrgChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const value = e.target.value as string;
    const orgDetails = _.find(customerOrgs, (org) => org?.id === value);
    const country = findCountry(orgDetails?.address?.country) ?? null;

    updateInvoice({
      existingOrg: orgDetails ? { label: orgDetails.name, value: orgDetails.id, slug: orgDetails.slug } : undefined,
      company: orgDetails?.name ?? '',
      organizationType: orgDetails?.organizationType ?? '',
      firstName: '',
      lastName: '',
      role: '',
      address1: orgDetails?.address?.line1 ?? '',
      address2: orgDetails?.address?.line2 ?? '',
      city: orgDetails?.address?.city ?? '',
      state: orgDetails?.address?.state ?? '',
      postalCode: orgDetails?.address?.zipCode ?? '',
    });

    handleCountryChange(country && { label: country.name, value: country.code });
  };

  const handleClearExistingOrg = () => {
    updateInvoice({
      existingOrg: undefined,
      company: '',
      organizationType: '',
      firstName: '',
      lastName: '',
      role: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      postalCode: '',
    });

    handleCountryChange(null);
  };

  const handleOrganizationTypeChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const value = e.target.value as string;
    updateInvoice({ organizationType: value });
  };

  const filteredTaxIdTypes = invoice.country ? stripeTaxIdTypeMap[invoice.country.value] ?? [] : STRIPE_TAX_ID_TYPES;

  const taxIdTypePlaceholder = selectedTaxIdType?.example
    ? `Example: ${selectedTaxIdType.example}`
    : 'Enter Tax/VAT Id';

  const handleTaxIdTypeChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const taxIdUniqueCode = e.target.value as string;

    const taxIdType = filteredTaxIdTypes.find((t) => t.uniqueCode === taxIdUniqueCode);

    updateInvoice({ taxIdTypeCode: taxIdType?.code || '' });
    setSelectedTaxIdType(taxIdType ?? null);
  };

  const checkRequiredFields = (keys: string[]) => {
    const errs: Record<string, string> = {};

    keys.forEach((key) => {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      if (!invoice[key] || !invoice[key].trim()) {
        errs[key] = 'This field is required.';
      }
    });

    if (Object.keys(errs).length > 0) {
      setErrors((prevState) => ({
        ...prevState,
        ...errs,
      }));
      return false;
    }

    return true;
  };

  const validateSeats = () => {
    const seats = parseInt(String(invoice.seats), 10);
    if (!seats || seats < 5 || seats > 500) {
      setErrors((prevState) => ({
        ...prevState,
        seats: 'This field must be a valid number between 5 ~ 500.',
      }));

      return false;
    }

    return true;
  };

  const validateCountry = () => {
    if (!invoice.country) {
      setErrors((prevState) => ({
        ...prevState,
        country: 'This field is required.',
      }));

      return false;
    }

    return true;
  };

  const validateTaxId = () => {
    if (invoice.taxId && !invoice.taxIdTypeCode) {
      setErrors((prevState) => ({
        ...prevState,
        taxIdTypeCode: 'This field is required.',
      }));
      return false;
    }

    if (!invoice.taxId && invoice.taxIdTypeCode) {
      setErrors((prevState) => ({
        ...prevState,
        taxId: 'This field is required.',
      }));
      return false;
    }

    return true;
  };

  const validateEmail = () => {
    const email = invoice.customerEmail;

    if (isEmail(email)) {
      setErrors((prevState) => ({
        ...prevState,
        customerEmail: 'Not a valid Email',
      }));

      return false;
    }

    return true;
  };

  const validateCompany = () => {
    if (!invoice.company || invoice.company.length < 1 || invoice.company.length > 80) {
      setErrors((prevState) => ({
        ...prevState,
        company: 'Company name must be between 1~80 characters.',
      }));

      return false;
    }

    return true;
  };

  const validateForm = () => {
    setErrors({});
    const requiredFields = [
      'company',
      'customerEmail',
      'organizationType',
      'billingType',
      'priceType',
      'address1',
      'city',
      'state',
      'postalCode',
    ];
    if (!invoice.existingOrg) {
      requiredFields.concat(['leadId']);
    }
    const requiredFieldsValid = checkRequiredFields(requiredFields);

    const companyValid = validateCompany();
    const seatsValid = validateSeats();
    const emailValid = validateEmail();
    const countryValid = validateCountry();
    const taxIdValid = validateTaxId();

    return requiredFieldsValid && companyValid && seatsValid && emailValid && countryValid && taxIdValid;
  };

  const handlePreviewInvoice = () => {
    const valid = validateForm();
    if (valid) {
      router.push(`/internal/enterprise/lead/invoice-preview/${invoice.leadId}`);
    }
  };

  if (!lead) {
    return null;
  }

  return (
    <Grid container css={styles.root} spacing={24}>
      <Grid item xs={12}>
        <Typography variant="d2">Create invoice from lead</Typography>
      </Grid>
      <Grid item xs={12}>
        <div className="c-radioGroupLabel">Owner</div>
        <RadioGroup value={invoice.owner} onChange={handleRadioChange('owner')}>
          <Radio value="COURSERA_SALES">Sales</Radio>
          <Radio value="MARKETSTAR">MarketStar</Radio>
          <Radio value="SCALEDSUCCESS">Scaled Success</Radio>
        </RadioGroup>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h2">Contract details</Typography>
      </Grid>
      <Grid item xs={12}>
        <SelectField
          label="Existing Orgs"
          value={invoice.existingOrg?.value ?? ''}
          onChange={handleExistingOrgChange}
          placeholder="Select an Existing Org"
          fullWidth
        >
          {customerOrgs.map((org) => (
            <SelectOption value={org?.id ?? ''}>{`${org?.name ?? ''} (${org?.slug ?? ''})`}</SelectOption>
          ))}
        </SelectField>
        {invoice.existingOrg?.value && invoice.existingOrg?.slug && (
          <div>
            <Link href={`/o/${invoice.existingOrg.slug}/admin/home`} target="_blank" rel="noreferrer">
              admin home
            </Link>
            {', '}
            <Link href="#" onClick={handleClearExistingOrg}>
              clear
            </Link>
          </div>
        )}
        <div className="c-radioGroupLabel">
          {errors.existingOrg && <span className="c-error">{errors.existingOrg}</span>}
        </div>
      </Grid>
      <Grid item xs={12}>
        <TextInput
          componentId="company"
          label="Company name"
          explanatoryText="(this will be used as the billing organization on the invoice - please ensure this is correct)"
          explanatoryTextPosition="before-input"
          placeholder="Enter company name"
          value={invoice.company}
          onChange={handleTextChange('company')}
          onBlur={handleTextBlur('company')}
          error={errors.company}
          disabled={!!invoice.existingOrg}
        />
      </Grid>
      <Grid item xs={12}>
        <SelectField
          label="Organization Type"
          value={invoice.organizationType}
          onChange={handleOrganizationTypeChange}
          placeholder="Select Organization Type"
        >
          {organizationTypes.map((orgType) => (
            <SelectOption value={orgType.value ?? ''}>{orgType.label}</SelectOption>
          ))}
        </SelectField>
        <div className="c-radioGroupLabel">
          {errors.organizationType && <span className="c-error">{errors.organizationType}</span>}
        </div>
      </Grid>
      {user.isSuperuserStrict() && (
        <Grid item xs={12} className="superuser">
          <Checkbox onChange={handleToggleEdit}>[SUPERUSER ONLY] Edit Email and SFDC ID</Checkbox>
        </Grid>
      )}
      <Grid item xs={12}>
        <TextInput
          componentId="customerEmail"
          label="Customer email (Coursera account)"
          placeholder="Enter customer's Email"
          value={invoice.customerEmail}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          error={errors.customerEmail}
          disabled={!allowSuperuserEdit}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          componentId="leadId"
          label="SFDC lead ID"
          placeholder="Enter Salesforce Lead ID"
          value={invoice.leadId}
          onChange={handleTextChange('leadId')}
          onBlur={handleTextBlur('leadId')}
          error={errors.leadId}
          disabled={!allowSuperuserEdit}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          componentId="seats"
          label="Seats (5-500)"
          placeholder="Enter number of seats"
          value={invoice.seats ? String(invoice.seats) : ''}
          onChange={handleSeatsChange}
          error={errors.seats}
        />
      </Grid>
      <Grid item xs={12}>
        <div className="c-radioGroupLabel">
          Billing type
          {errors.billingType && <span className="c-error">{errors.billingType}</span>}
        </div>
        <RadioGroup value={invoice.billingType} onChange={handleRadioChange('billingType')} disabled>
          <Radio value="annual">Annual</Radio>
        </RadioGroup>
      </Grid>
      <Grid item xs={12}>
        <div className="c-radioGroupoLabel">
          Price (This field will be automatically filled after Country is selected)
          {errors.priceType && <span className="c-error">{errors.priceType}</span>}
        </div>
        <RadioGroup value={invoice.priceType} onChange={handleRadioChange('priceType')} disabled>
          <Radio value="standard">Standard</Radio>
          <Radio value="geo">
            Geo-price {invoice.priceType === 'geo' ? `(${invoice.currencyCode} $${invoice.unitPrice})` : ''}
          </Radio>
        </RadioGroup>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h2">Invoice details</Typography>
        <Typography2 component="p" variant="bodySecondary">
          These details will be added to the invoice-funded contract and returning invoices, if set to auto-renew.
        </Typography2>
      </Grid>
      <Grid item xs={6}>
        <TextInput
          componentId="firstName"
          label="First name"
          placeholder="Enter first name"
          value={invoice.firstName}
          onChange={handleTextChange('firstName')}
          onBlur={handleTextBlur('firstName')}
          error={errors.firstName}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          componentId="lastName"
          label="Last name"
          placeholder="Enter last name"
          value={invoice.lastName}
          onChange={handleTextChange('lastName')}
          onBlur={handleTextBlur('lastName')}
          error={errors.lastName}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          componentId="role"
          label="Role (optional)"
          placeholder="Enter role name"
          value={invoice.role}
          onChange={handleTextChange('role')}
          onBlur={handleTextBlur('role')}
          error={errors.role}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          componentId="address1"
          label="Street address"
          placeholder="Enter street number and street address"
          value={invoice.address1}
          onChange={handleTextChange('address1')}
          onBlur={handleTextBlur('address1')}
          error={errors.address1}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          componentId="address2"
          label="Address, line 2 (optional)"
          placeholder=""
          value={invoice.address2}
          onChange={handleTextChange('address2')}
          onBlur={handleTextBlur('address2')}
          error={errors.address2}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          componentId="city"
          label="City"
          placeholder="Enter city name"
          value={invoice.city}
          onChange={handleTextChange('city')}
          onBlur={handleTextBlur('city')}
          error={errors.city}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          componentId="state"
          label="State / Province / Region"
          placeholder="Enter state /province / region name"
          value={invoice.state}
          onChange={handleTextChange('state')}
          onBlur={handleTextBlur('state')}
          error={errors.state}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          componentId="postalCode"
          label="Postal / ZIP code"
          placeholder="Enter postal code or zip code"
          value={invoice.postalCode}
          onChange={handleTextChange('postalCode')}
          onBlur={handleTextBlur('postalCode')}
          error={errors.postalCode}
        />
      </Grid>
      <Grid item xs={6}>
        <div className="c-radioGroupLabel">
          Country (must select from the dropdown)
          {errors.country && <span className="c-error">{errors.country}</span>}
        </div>

        <Autocomplete
          value={invoice.country?.value ?? ''}
          options={countries.map((nameAndCode: $TSFixMe) => ({
            label: `${nameAndCode.name} (${nameAndCode.code})`,
            value: nameAndCode.code,
          }))}
          onChange={handleCountryChange}
        />
      </Grid>
      <Grid item xs={6}>
        <SelectField
          label="Tax Id Type (mandatory if Tax ID is provided)"
          value={selectedTaxIdType?.uniqueCode}
          onChange={handleTaxIdTypeChange}
        >
          {filteredTaxIdTypes.map((taxIdType) => (
            <SelectOption key={taxIdType.uniqueCode} value={taxIdType.uniqueCode}>
              {taxIdType.country} - {taxIdType.name}({taxIdType.code})
            </SelectOption>
          ))}
        </SelectField>
        <div className="c-radioGroupLabel">
          {errors.taxIdTypeCode && <span className="c-error">{errors.taxIdTypeCode}</span>}
        </div>
      </Grid>
      <Grid item xs={6}>
        <TextInput
          componentId="taxId"
          label="Tax/VAT ID (mandatory if Tax Id Type is provided)"
          placeholder={taxIdTypePlaceholder}
          value={invoice.taxId}
          onChange={handleTextChange('taxId')}
          onBlur={handleTextBlur('taxId')}
          error={errors.taxId}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          componentId="poNumber"
          label="PO Number (optional)"
          placeholder=""
          value={invoice.poNumber}
          onChange={handleTextChange('poNumber')}
          onBlur={handleTextBlur('poNumber')}
          error={errors.poNumber}
          nativeHtmlAttributes={{ maxLength: 30 }}
        />
      </Grid>
      <Grid item xs={12}>
        <EnterpriseLeadInvoiceSummary
          unitPrice={invoice.unitPrice}
          seats={invoice.seats}
          amountTotal={invoice.amountTotal}
          currencyCode={invoice.currencyCode}
        />
      </Grid>
      <Grid item xs={12}>
        <Button text="Preview invoice details" btnStyle="info" onClick={handlePreviewInvoice} />
      </Grid>
    </Grid>
  );
};

export default EnterpriseLeadInvoiceForm;
